import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Dayjs } from "dayjs";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { featureAccess } from "../permissions/access";
import { useUserRole } from "../utils/hooks/useUserRole";
import { useDebounce } from "../utils/hooks/useDebounce";
import SpecialOrderFilter, { ALL } from "../components/SpecialOrder/Filter";
import SpecialOrderTable from "../components/SpecialOrder/Table";
import CloseDayPanel from "../components/SpecialOrder/CloseDayPanel";
import { useBranches } from "../components/SpecialOrder/useBranches";
import { SPECIAL_ORDER_NEW_PATH } from "../components/SpecialOrder/data";

const TAB_PARAM = "tab";
const CLOSE_DAY_TAB = "close-day";

export default function SpecialOrderListPage() {
  const navigate = useNavigate();
  const role = useUserRole();
  const canSelectBranch = featureAccess.specialOrderAllBranches(role);
  const branches = useBranches(canSelectBranch);

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get(TAB_PARAM) === CLOSE_DAY_TAB ? CLOSE_DAY_TAB : "orders";

  const [companyId, setCompanyId] = useState<string>(ALL);
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [status, setStatus] = useState<string>(ALL);
  const [routeUuid, setRouteUuid] = useState<string>(ALL);
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 500);

  const handleChangeTab = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === CLOSE_DAY_TAB) next.set(TAB_PARAM, CLOSE_DAY_TAB);
    else next.delete(TAB_PARAM);
    setSearchParams(next, { replace: true });
  };

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        mb={2}
        flexWrap="wrap"
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            ออเดอร์พิเศษ
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ออเดอร์ที่หลังบ้านออกให้ลูกค้า · แบบ A สายส่งไปส่ง · แบบ B ลูกค้ามารับที่สาขา (สายพิเศษ)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(SPECIAL_ORDER_NEW_PATH)}
        >
          สร้างออเดอร์พิเศษ
        </Button>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, value) => handleChangeTab(value)}
        sx={{ mb: 2 }}
      >
        <Tab value="orders" label="รายการออเดอร์" />
        <Tab value={CLOSE_DAY_TAB} label="ปิดวันสายพิเศษ" />
      </Tabs>

      {tab === "orders" ? (
        <>
          <Paper sx={{ padding: "24px 16px", mb: 2 }}>
            <SpecialOrderFilter
              canSelectBranch={canSelectBranch}
              branches={branches}
              companyId={companyId}
              setCompanyId={setCompanyId}
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
              status={status}
              setStatus={setStatus}
              routeUuid={routeUuid}
              setRouteUuid={setRouteUuid}
              search={search}
              setSearch={setSearch}
            />
          </Paper>
          <SpecialOrderTable
            companyId={companyId}
            dateFrom={dateFrom}
            dateTo={dateTo}
            status={status}
            routeUuid={routeUuid}
            search={debouncedSearch}
          />
        </>
      ) : (
        <CloseDayPanel canSelectBranch={canSelectBranch} branches={branches} />
      )}
    </Box>
  );
}
